import type { IUserSearchItem } from "@frontend-shared/types";
import { ChevronDown, Loader2, X } from "lucide-react";
import { memo, useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Command, CommandGroup, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { ShareExtraOptionItem } from "./ShareExtraOptionItem";
import type { IShareInviteInputProps } from "./types";
import { UserSuggestionItem } from "./UserSuggestionItem";

const ShareInviteInputComponent = ({
  selectedUsers,
  searchQuery,
  onSearchQueryChange,
  onToggleUser,
  onRemoveUser,
  onClearSelectedUsers,
  onInvite,
  onKeyDown,
  onOpenDropdown,
  onToggleDropdown,
  isSearching,
  isSharing,
  canInviteCurrent,
  isDropdownVisible,
  hasResults,
  filteredProjectMembers,
  filteredOrgMembers,
  highlightedValue,
  inputRef,
  suggestionListRef,
  containerRef,
  submitDisabled = false,
  selectionLimit,
  inviteAdornment,
  extraGroup,
  labels,
}: IShareInviteInputProps): JSX.Element => {
  const selectedExtraOptions = useMemo(
    () => extraGroup?.selectedOptions ?? [],
    [extraGroup?.selectedOptions],
  );
  const extraOptions = useMemo(
    () => extraGroup?.options ?? [],
    [extraGroup?.options],
  );
  // NOTE: NSAI-6501 — the pending batch is people AND extra options, so the
  // count, the "clear all" row and the Invite button all read both.
  const selectedCount = selectedUsers.length + selectedExtraOptions.length;
  const hasSelectedUsers = selectedCount > 0;
  const isInputDisabled = !canInviteCurrent;
  const shouldShowSuggestions = isDropdownVisible;
  // NOTE: At the cap the picker keeps working — the query, the chips and the
  // ticked rows all stay usable — it just refuses to grow the selection, so the
  // user swaps someone out instead of hitting a server-side rejection.
  const isSelectionLimitReached =
    selectionLimit !== undefined && selectedUsers.length >= selectionLimit.max;

  const selectedUserUuids = useMemo(
    () => new Set(selectedUsers.map((selectedUser) => selectedUser.uuid)),
    [selectedUsers],
  );

  const selectedExtraIds = useMemo(
    () => new Set(selectedExtraOptions.map((option) => option.id)),
    [selectedExtraOptions],
  );

  const renderMemberOption = (user: IUserSearchItem): JSX.Element => (
    <UserSuggestionItem
      key={user.uuid}
      user={user}
      isSelected={selectedUserUuids.has(user.uuid)}
      isDisabled={isSelectionLimitReached && !selectedUserUuids.has(user.uuid)}
      onToggle={onToggleUser}
      unknownUserLabel={labels.unknownUser}
      selectAriaLabel={labels.formatSelectMemberAria({
        name: user.fullName || user.email,
      })}
      unselectAriaLabel={labels.formatUnselectMemberAria({
        name: user.fullName || user.email,
      })}
    />
  );

  return (
    <div className="flex items-end gap-2 py-0">
      {/* NOTE: The outside-click guard is anchored here, not on the field
      wrapper below, so the selected-count row and its "unselect all" button
      count as part of the control — clearing a selection must not collapse the
      members list the user is still working through. */}
      <div ref={containerRef} className="flex flex-1 flex-col gap-1.5">
        {hasSelectedUsers && (
          <div className="flex items-center justify-end gap-3 text-xs text-muted-foreground">
            <span data-testid="share-selected-count">
              {labels.formatSelectedCount({
                current: selectedCount,
              })}
            </span>
            <Button
              type="button"
              variant="link"
              onClick={onClearSelectedUsers}
              className="h-auto p-0 text-xs underline-offset-2"
            >
              {labels.clearAll}
            </Button>
          </div>
        )}

        <div className="relative">
          <div className="flex max-h-20 min-h-10 w-full flex-wrap items-center gap-1.5 overflow-y-auto rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
            {selectedExtraOptions.map((option) => (
              <Badge
                key={option.id}
                variant="secondary"
                data-testid="share-selected-extra-chip"
                className="flex items-center gap-1 px-2 py-0.5 text-xs"
              >
                <span>{option.label}</span>
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => extraGroup?.onRemove(option.id)}
                />
              </Badge>
            ))}

            {selectedUsers.map((selectedUser) => (
              <Badge
                key={selectedUser.uuid}
                variant="secondary"
                data-testid="share-selected-chip"
                className="flex items-center gap-1 px-2 py-0.5 text-xs"
              >
                <span>{selectedUser.fullName || selectedUser.email}</span>
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => onRemoveUser(selectedUser.uuid)}
                />
              </Badge>
            ))}

            <Input
              ref={inputRef}
              value={searchQuery}
              // NOTE: Typing and clicking the field are explicit requests for
              // the members list, so both reveal it. Focus alone does not —
              // otherwise the dialog's autofocus would expand the list before
              // the user asked for it (NSAI-5025).
              onChange={(event) => {
                onSearchQueryChange(event.target.value);
                onOpenDropdown();
              }}
              onClick={onOpenDropdown}
              onKeyDown={onKeyDown}
              placeholder={labels.placeholder}
              aria-label={labels.inputAriaLabel}
              className="h-7 min-h-0 min-w-[8rem] flex-1 border-0 bg-transparent p-0 shadow-none outline-none placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
              disabled={isInputDisabled}
            />

            {isSearching && (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
            )}

            <Button
              type="button"
              variant="ghost"
              onClick={onToggleDropdown}
              disabled={isInputDisabled}
              aria-expanded={isDropdownVisible}
              aria-label={
                isDropdownVisible ? labels.hideMembers : labels.showMembers
              }
              data-testid="share-members-toggle"
              className="h-6 w-6 shrink-0 p-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
            >
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  isDropdownVisible && "rotate-180",
                )}
              />
            </Button>
          </div>

          {shouldShowSuggestions && !isSearching && !hasResults && (
            <div className="absolute end-0 start-0 top-full z-50 rounded-md border bg-popover p-4 text-popover-foreground shadow-md">
              <p className="text-center text-sm text-muted-foreground">
                {labels.noResults}
              </p>
            </div>
          )}

          {shouldShowSuggestions && hasResults && (
            <div
              ref={suggestionListRef}
              data-testid="share-members-dropdown"
              className="absolute end-0 start-0 top-full z-50 max-h-[min(32rem,70vh)] overflow-auto rounded-md border bg-popover p-0 text-popover-foreground shadow-md"
            >
              <Command shouldFilter={false} value={highlightedValue}>
                <CommandList>
                  {extraGroup !== undefined && extraOptions.length > 0 && (
                    <CommandGroup heading={extraGroup.heading}>
                      {extraOptions.map((option) => (
                        <ShareExtraOptionItem
                          key={option.id}
                          option={option}
                          icon={extraGroup.icon}
                          isSelected={selectedExtraIds.has(option.id)}
                          onToggle={extraGroup.onToggle}
                          selectAriaLabel={extraGroup.formatSelectAria({
                            name: option.label,
                          })}
                          unselectAriaLabel={extraGroup.formatUnselectAria({
                            name: option.label,
                          })}
                        />
                      ))}
                    </CommandGroup>
                  )}

                  {filteredProjectMembers.length > 0 && (
                    <CommandGroup heading={labels.projectMembersHeading}>
                      {filteredProjectMembers.map(renderMemberOption)}
                    </CommandGroup>
                  )}

                  {filteredOrgMembers.length > 0 && (
                    <CommandGroup heading={labels.organizationMembersHeading}>
                      {filteredOrgMembers.map(renderMemberOption)}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </div>
          )}
        </div>

        {isSelectionLimitReached && (
          <p
            className="text-xs text-muted-foreground"
            data-testid="share-selection-limit-notice"
          >
            {selectionLimit?.reachedMessage}
          </p>
        )}
      </div>

      {inviteAdornment !== undefined && (
        <div className="shrink-0">{inviteAdornment}</div>
      )}

      <Button
        onClick={onInvite}
        disabled={
          !canInviteCurrent ||
          isSharing ||
          selectedCount === 0 ||
          submitDisabled
        }
        className="shrink-0 rounded-md"
      >
        {labels.invite}
      </Button>
    </div>
  );
};

export const ShareInviteInput = memo(ShareInviteInputComponent);

ShareInviteInput.displayName = "ShareInviteInput";
